import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { Trans } from "react-i18next";
import { Typography } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";
import Lottie from "lottie-react";
import { useMobileLayout } from "@verity/components";
import {
	CoreButton,
	CoreDatePicker,
	CoreModal,
	CoreNoResults,
	CoreSearchField,
	CoreSearchFieldPaper,
	CoreTable,
	CoreTitleCard,
	coreToast,
} from "@verity/components/core";
import { ApiClientResponseError } from "@verity/api-client";
import { AssessmentQuestion } from "@verity/api-client/types";
import apiClient from "../../../helpers/api-client";
import i18n from "../../../i18n";
import { extractAssessmentName } from "../../../helpers/assessment-name";
import ModalPage from "../modal-page";
import BackButton from "../../../components/back-button";
import PageCard from "../page-card";
import TextSkeleton from "../../../utility/TextSkeleton";
import { PORTAL_TOAST_CONTAINER_ID } from "../../shared";
import { QuizWithProctoringOptions } from "./assessments-page-helper";
import styles from "./assessment-decoy-modal.module.css";
import EyeLock from "../../../images/portal/eye-lock.svg?react";
import CheckCircle from "../../../images/check-circle2.svg?react";
import Bell from "../../../images/portal/bell.svg?react";
import AddBox from "../../../images/portal/add-box.svg?react";
import ArrowRight from "../../../images/reports/arrowRight.svg?react";
import loadingDashCircle from "../../../images/portal/loadingDashCircle.json";

type DecoySiteEvent = {
	id: number;
	question?: string;
	user?: { id: number; name?: string };
	ipAddress?: string;
	time: Date;
};

function DeactivateConfirmationModal({ onClose, onDeactivate }: { onClose: () => void; onDeactivate: () => void }) {
	return (
		<CoreModal
			open
			onClose={onClose}
			title={i18n.t("portal.components.assessmentDecoyModalTSX.confirmDeactivate.title")}
			buttons={
				<>
					<CoreButton color="secondary" onClick={onClose}>
						{i18n.t("portal.components.assessmentDecoyModalTSX.confirmDeactivate.cancel")}
					</CoreButton>
					<CoreButton
						color="cancel"
						onClick={() => {
							onDeactivate();
							onClose();
						}}
					>
						{i18n.t("portal.components.assessmentDecoyModalTSX.confirmDeactivate.deactivate")}
					</CoreButton>
				</>
			}
			maxWidth="600px"
		>
			{i18n.t("portal.components.assessmentDecoyModalTSX.confirmDeactivate.description")}
		</CoreModal>
	);
}

function ActivitySection({ assessmentId }: { assessmentId: number }) {
	const [searchQuery, setSearchQuery] = useState("");
	const [startTime, setStartTime] = useState<Date | undefined>(undefined);
	const [endTime, setEndTime] = useState<Date | undefined>(undefined);

	const [events, setEvents] = useState<DecoySiteEvent[]>([]);
	const [loading, setLoading] = useState(true);
	const [errorLoading, setErrorLoading] = useState(false);
	useEffect(() => {
		(async () => {
			setLoading(true);
			try {
				const fetchedEvents = (
					await apiClient.proctoringSession.getProctoringEvents({
						assessmentIds: [assessmentId],
						types: [{ type: "user action", name: "decoy site viewed" }],
					})
				).results;
				const users = Object.fromEntries(
					(
						await Promise.all(
							[...new Set(fetchedEvents.map(({ proctoringSession }) => proctoringSession.studentId))].map((userId) =>
								apiClient.user.getUserInfo(userId),
							),
						)
					).map((user) => [user.id, user]),
				);
				setEvents(
					fetchedEvents.map(({ id, details, proctoringSession, time }) => {
						const user = users[proctoringSession.studentId];
						return {
							id,
							question: details.question?.bodyText || details.question?.title,
							user: user ? { ...user, name: user.userInfo?.full_name } : undefined,
							ipAddress: proctoringSession.ipAddress ?? undefined,
							time: new Date(time),
						};
					}),
				);
			} catch (e) {
				console.error(e);
				setErrorLoading(true);
			} finally {
				setLoading(false);
			}
		})();
	}, [assessmentId]);

	const filteredEvents = useMemo(() => {
		const lowerSearchQuery = searchQuery.toLowerCase();
		const inclusiveEndTime = endTime ? new Date(endTime.getTime() + 24 * 60 * 60 * 1000) : undefined;
		return events.filter(
			({ question, user, time }) =>
				(!lowerSearchQuery ||
					question?.toLowerCase().includes(lowerSearchQuery) ||
					user?.name?.toLowerCase().includes(lowerSearchQuery)) &&
				(!startTime || time >= startTime) &&
				(!inclusiveEndTime || time <= inclusiveEndTime),
		);
	}, [events, searchQuery, startTime, endTime]);

	const tableId = useId();
	const noResults = !loading && !errorLoading && events.length === 0;
	const loaded = !noResults && !errorLoading;

	return (
		<PageCard
			className={styles.activityCard}
			icon={<Bell />}
			title={i18n.t("portal.components.assessmentDecoyModalTSX.activity.title")}
		>
			<div className={`${styles.activityCardContent} ${loaded ? "" : styles.empty}`}>
				{noResults && (
					<CoreNoResults
						header={i18n.t("portal.components.assessmentDecoyModalTSX.noActivity.title")}
						image={<AddBox />}
						gap="40px"
					>
						{i18n.t("portal.components.assessmentDecoyModalTSX.noActivity.description")}
					</CoreNoResults>
				)}
				{errorLoading && (
					<CoreNoResults
						variant="failed"
						header={i18n.t("portal.components.assessmentDecoyModalTSX.activityLoadingFailed.title")}
						gap="40px"
					>
						{i18n.t("portal.components.assessmentDecoyModalTSX.activityLoadingFailed.description")}
					</CoreNoResults>
				)}
				{loaded && (
					<>
						<Typography className={styles.description}>
							{i18n.t("portal.components.assessmentDecoyModalTSX.activity.description")}
						</Typography>
						<CoreSearchFieldPaper className={styles.searchBar}>
							<CoreSearchField
								size="small"
								className={styles.searchField}
								value={searchQuery}
								onSearch={setSearchQuery}
								label={i18n.t("portal.components.assessmentDecoyModalTSX.searchBy")}
								ariaControls={tableId}
								totalResults={filteredEvents.length}
							/>
							<div className={styles.dateArea}>
								<span>{i18n.t("portal.components.assessmentDecoyModalTSX.filterDate")}</span>
								<CoreDatePicker
									disableFuture
									value={startTime ?? null}
									maxDate={endTime}
									onChange={(time) => setStartTime(time ?? undefined)}
									ariaLabel={i18n.t("portal.components.assessmentDecoyModalTSX.startDate")}
									dialogAriaLabel={i18n.t("portal.components.assessmentDecoyModalTSX.startDateSelect")}
									slotProps={{ textField: { size: "small", className: styles.dateField } }}
								/>
								<SvgIcon>
									<ArrowRight />
								</SvgIcon>
								<CoreDatePicker
									disableFuture
									value={endTime ?? null}
									minDate={startTime}
									onChange={(time) => setEndTime(time ?? undefined)}
									ariaLabel={i18n.t("portal.components.assessmentDecoyModalTSX.endDate")}
									dialogAriaLabel={i18n.t("portal.components.assessmentDecoyModalTSX.endDateSelect")}
									slotProps={{ textField: { size: "small", className: styles.dateField } }}
								/>
							</div>
						</CoreSearchFieldPaper>
						<CoreTable
							data={filteredEvents}
							columns={[
								{
									key: "user",
									header: i18n.t("portal.components.assessmentDecoyModalTSX.student"),
									cell: (row) => row.data.user?.name,
									descendingComparator: (a, b) => (a.user?.name ?? "").localeCompare(b.user?.name ?? ""),
									truncateString: true,
									showTooltip: true,
									width: "30%",
								},
								{
									key: "question",
									header: i18n.t("portal.components.assessmentDecoyModalTSX.question"),
									cell: (row) => row.data.question,
									descendingComparator: (a, b) => (a.question ?? "").localeCompare(b.question ?? ""),
									truncateString: true,
									showTooltip: true,
									width: "70%",
								},
								{
									key: "ip",
									header: i18n.t("portal.components.assessmentDecoyModalTSX.ipAddress"),
									cell: (row) => row.data.ipAddress,
									descendingComparator: (a, b) => (a.ipAddress ?? "").localeCompare(b.ipAddress ?? ""),
									width: 120,
								},
								{
									key: "time",
									header: i18n.t("portal.components.assessmentDecoyModalTSX.time"),
									cell: (row) => row.data.time.toLocaleString(),
									descendingComparator: (a, b) => a.time.getTime() - b.time.getTime(),
									width: 250,
								},
							]}
							loading={loading}
							noResultsTitle={i18n.t("portal.components.assessmentDecoyModalTSX.noFilteredActivity.title")}
							noResultsString={i18n.t("portal.components.assessmentDecoyModalTSX.noFilteredActivity.description")}
						/>
					</>
				)}
			</div>
		</PageCard>
	);
}

export default function AssessmentDecoyModal({
	assessment,
	onUpdate,
	onBack,
}: {
	assessment: QuizWithProctoringOptions;
	onUpdate: () => void;
	onBack: () => void;
}) {
	const isMobileLayout = useMobileLayout();
	const assessmentName = extractAssessmentName(assessment);

	const [questions, setQuestions] = useState<AssessmentQuestion[]>([]);
	const [noLmsQuestions, setNoLmsQuestions] = useState(false);
	const [loading, setLoading] = useState(true);
	const activated = questions.some(({ hasDecoySitePost }) => hasDecoySitePost);
	const assessmentId = assessment.id;
	const updateQuestions = useCallback(async () => {
		setQuestions(await apiClient.assessment.getAssessmentQuestions(assessmentId, true));
	}, [assessmentId]);
	useEffect(() => {
		(async () => {
			setLoading(true);
			try {
				await updateQuestions();
			} catch (e) {
				console.error(e);
			} finally {
				setLoading(false);
			}
		})();
	}, [updateQuestions]);

	const [processingAction, setProcessingAction] = useState<"activating" | "deactivating" | undefined>(undefined);
	const activate = async () => {
		setProcessingAction("activating");
		try {
			await apiClient.assessment.createDecoySite(assessment.id);
			await updateQuestions();
			coreToast.success(i18n.t("portal.components.assessmentDecoyModalTSX.activateSuccess", { assessmentName }), {
				containerId: PORTAL_TOAST_CONTAINER_ID,
			});
		} catch (e) {
			console.error(e);
			if (e instanceof ApiClientResponseError && e.code === "noQuestions") {
				setNoLmsQuestions(true);
			} else {
				coreToast.error(i18n.t("portal.components.assessmentDecoyModalTSX.activateFailed"), {
					containerId: PORTAL_TOAST_CONTAINER_ID,
				});
			}
		} finally {
			setProcessingAction(undefined);
		}
		onUpdate();
	};
	const deactivate = async () => {
		setProcessingAction("deactivating");
		try {
			await apiClient.assessment.deleteDecoySite(assessment.id);
			await updateQuestions();
			coreToast.success(i18n.t("portal.components.assessmentDecoyModalTSX.deactivateSuccess", { assessmentName }), {
				containerId: PORTAL_TOAST_CONTAINER_ID,
			});
		} catch (e) {
			console.error(e);
			coreToast.error(i18n.t("portal.components.assessmentDecoyModalTSX.deactivateFailed"), {
				containerId: PORTAL_TOAST_CONTAINER_ID,
			});
		} finally {
			setProcessingAction(undefined);
		}
		onUpdate();
	};

	const [deactivateModalShowing, setDeactivateModalShowing] = useState(false);

	return (
		<ModalPage>
			<div className={`${styles.page} ${isMobileLayout ? styles.compact : ""}`}>
				<div>
					<BackButton onBack={onBack} />
				</div>
				<CoreTitleCard title={assessmentName} slotProps={{ title: { className: styles.title } }} />
				<PageCard
					className={styles.descriptionCard}
					icon={<EyeLock />}
					title={
						<div className={styles.descriptionCardHeader}>
							{loading ? (
								<TextSkeleton />
							) : (
								<Typography variant="h2" className={styles.descriptionCardHeaderText}>
									{activated
										? i18n.t("portal.components.assessmentDecoyModalTSX.activatedTitle")
										: i18n.t("portal.components.assessmentDecoyModalTSX.title")}
								</Typography>
							)}
							{activated && (
								<div className={styles.activatedIndicator}>
									<SvgIcon>
										<CheckCircle />
									</SvgIcon>
									{i18n.t("portal.components.assessmentDecoyModalTSX.activated")}
								</div>
							)}
						</div>
					}
					titleVariant="body1"
				>
					<div className={styles.descriptionCardContent}>
						{loading && <TextSkeleton width="100%" />}
						{noLmsQuestions && (
							<CoreNoResults
								variant="no-results-variant"
								header={i18n.t("portal.components.assessmentDecoyModalTSX.noQuestions.title")}
								gap="20px"
							>
								{i18n.t("portal.components.assessmentDecoyModalTSX.noQuestions.description")}
							</CoreNoResults>
						)}
						{!loading && !noLmsQuestions && (
							<div className={styles.descriptionArea}>
								<Typography className={styles.description}>
									{activated ? (
										i18n.t("portal.components.assessmentDecoyModalTSX.activatedDescription")
									) : (
										<Trans
											i18nKey="portal.components.assessmentDecoyModalTSX.description"
											values={{ assessmentName }}
											components={{ strong: <strong /> }}
										/>
									)}
								</Typography>
								<CoreButton
									color={activated ? "cancel" : undefined}
									className={styles.actionButton}
									onClick={activated ? () => setDeactivateModalShowing(true) : activate}
									disabled={!!processingAction}
								>
									{activated
										? i18n.t("portal.components.assessmentDecoyModalTSX.deactivate")
										: i18n.t("portal.components.assessmentDecoyModalTSX.activate")}
								</CoreButton>
							</div>
						)}
						{processingAction && (
							<div className={styles.processingArea} role="status" aria-live="polite" aria-busy="true">
								<Lottie className={styles.processingAnimation} animationData={loadingDashCircle} />
								<div className={styles.processingTextArea}>
									<div className={styles.processingTitle}>
										{i18n.t(`portal.components.assessmentDecoyModalTSX.${processingAction}.title`)}
									</div>
									{i18n.t(`portal.components.assessmentDecoyModalTSX.${processingAction}.description`)}
								</div>
							</div>
						)}
					</div>
				</PageCard>
				{activated && !processingAction && <ActivitySection assessmentId={assessment.id} />}
			</div>
			{deactivateModalShowing && (
				<DeactivateConfirmationModal onClose={() => setDeactivateModalShowing(false)} onDeactivate={deactivate} />
			)}
		</ModalPage>
	);
}
